import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { projectsAPI, backlogTasksAPI, dailyPlansAPI } from '../services/api';
import AddTaskModal from './AddTaskModal';
import FreeTimeModal from './FreeTimeModal';
import QuickLogModal from './QuickLogModal';
import { useNavigate } from 'react-router-dom';

const TaskPlanner = () => {
  const { user, logout } = useAuth();

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [backlogTasks, setBacklogTasks] = useState([]);
  const [dailyPlan, setDailyPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showFreeTimeModal, setShowFreeTimeModal] = useState(false);
  const navigate = useNavigate();

  const [estimates, setEstimates] = useState({});

  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [quickLogTask, setQuickLogTask] = useState(null);


  useEffect(() => {
    loadProjects();
    loadTodayPlan();
  }, []);

  // Refresh plan when navigating back to this page (e.g. after submitting logs)
  useEffect(() => {
    const handleFocus = () => loadTodayPlan();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  useEffect(() => {
    if (selectedProject) {
      loadBacklogTasks(selectedProject);
    } else {
      setBacklogTasks([]);
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadBacklogTasks = async (projectId) => {
    try {
      const response = await backlogTasksAPI.getByProject(projectId);
      setBacklogTasks(response.data);
    } catch (error) {
      console.error('Failed to load backlog tasks:', error);
    }
  };

  const loadTodayPlan = async () => {
    try {
      const response = await dailyPlansAPI.getToday();
      setDailyPlan(response.data);
    } catch (error) {
      console.error('Failed to load daily plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTaskToPlan = async (backlogTask) => {
    const estimateMinutes = parseFloat(estimates[backlogTask.id] ?? '60');

    if (!estimateMinutes || estimateMinutes <= 0) {
      alert('Please select a valid estimate');
      return;
    }

    const estimateHours = estimateMinutes / 60;

    if (dailyPlan.totalPlannedHours + estimateHours > 8 && !dailyPlan.checkedIn) {
      alert(`Cannot add task. Total would exceed 8 hours (current: ${dailyPlan.totalPlannedHours} hrs)`);
      return;
    }

    try {
      const response = await dailyPlansAPI.addTask({
        backlogTaskId: backlogTask.id,
        title: backlogTask.title,
        description: backlogTask.description,
        priority: backlogTask.priority,
        type: backlogTask.type,
        estimate: estimateHours
      });

      setDailyPlan(response.data);
      setEstimates(prev => ({ ...prev, [backlogTask.id]: '60' }));
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add task');
    }
  };

  const removeTaskFromPlan = async (taskId) => {
    try {
      const response = await dailyPlansAPI.removeTask(taskId);
      setDailyPlan(response.data);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to remove task');
    }
  };

  // const handleCheckIn = async () => {
  //   if (dailyPlan.totalPlannedHours < 4) {
  //     alert('Can only check-in with at least 4 hours planned');
  //     return;
  //   }

  //   try {
  //     const response = await dailyPlansAPI.checkIn();
  //     setDailyPlan(response.data);
  //     alert('Checked in successfully! Attendance marked.');
  //   } catch (error) {
  //     alert(error.response?.data?.message || 'Failed to check in');
  //   }
  // };
  const handleCheckIn = async () => {
    if (checkingIn) return;

    setCheckingIn(true);

    try {
      const response = await dailyPlansAPI.checkIn();
      setDailyPlan(response.data);
      setTimeout(() => {
        alert('✅ Checked in successfully!');
      }, 100);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to check in');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleAddNewTask = async (taskData) => {
    try {
      if (taskData.id) {
        await backlogTasksAPI.update(taskData.id, taskData);
      } else {
        await backlogTasksAPI.create({
          ...taskData,
          projectId: selectedProject
        });
      }
      await loadBacklogTasks(selectedProject);
      setShowAddTaskModal(false);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create task');
    }
  };

  const handleAddFreeTime = async (freeTimeData) => {
    try {
      const response = await dailyPlansAPI.addTask({
        title: freeTimeData.title,
        description: 'Free time / break',
        type: 'Break',
        estimate: freeTimeData.hours,
        isFreeTime: true
      });

      setDailyPlan(response.data);
      setShowFreeTimeModal(false);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add free time');
    }
  };

  const handleCheckOut = async () => {
    // Guard: logs must be submitted first
    if (!dailyPlan?.logsSubmitted) {
      alert('Please submit your logs before checking out.');
      return;
    }

    // Guard: already checked out
    if (dailyPlan?.checkedOut) {
      alert('You have already checked out for today.');
      return;
    }

    const totalLogged = getTotalLoggedHours();

    //  Confirmation popup
    const confirmCheckout = window.confirm(
      `You have entered ${totalLogged.toFixed(2)} hrs of log.\nAre you sure you want to check out?`
    );

    if (!confirmCheckout) return;

    if (checkingOut) return;
    setCheckingOut(true);

    try {
      await dailyPlansAPI.checkOut();
      await loadTodayPlan();
      alert('Checked out successfully! Have a great evening 👋');
    } catch (error) {
      alert(error.response?.data?.message || 'Checkout failed');
    } finally {
      setCheckingOut(false);
    }
  };

  const getTotalPlannedHours = () => {
    return dailyPlan?.totalPlannedHours || 0;
  };

  const getTimeBadgeClass = () => {
    const total = getTotalPlannedHours();
    if (total === 8) return 'time-badge full';
    if (total > 8) return 'time-badge over';
    return 'time-badge';
  };

  const getCheckInButtonClass = () => {
    const total = getTotalPlannedHours();
    return total >= 4 && !dailyPlan?.checkedIn ? 'checkin-button enabled' : 'checkin-button';
  };

  const canCheckIn = () => {
    return getTotalPlannedHours() >= 4 && !dailyPlan?.checkedIn;
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const availableBacklogTasks = backlogTasks.filter(task =>
    !dailyPlan?.tasks.some(pt => pt.backlogTask?.id === task.id)
  );

  //  Derived state for cleaner JSX
  const isCheckedIn = dailyPlan?.checkedIn;
  const isLogsSubmitted = dailyPlan?.logsSubmitted;
  const isCheckedOut = dailyPlan?.checkedOut;

  const hasAtLeastOneLog =
    dailyPlan?.tasks?.some(
      task => task.notes && task.notes.trim() !== ''
    );


  const formatHoursToHHMM = (hours) => {
    const totalMinutes = Math.round(hours * 60);

    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    const formattedMinutes = m.toString().padStart(2, '0');

    return `${h}.${formattedMinutes}`;
  };

  const getTotalLoggedHours = () => {
    return dailyPlan?.tasks?.reduce((sum, task) => {
      return sum + (task.actualHours || 0);
    }, 0) || 0;
  };
  
  return (
    <div className="app-container">
      <div className="container">
        <div className="header">
          <div className="header-top">

            {/* LEFT */}
            <div className="user-info">
              <span className="user-name">{user?.name}</span>
              <span style={{ fontSize: '12px', opacity: 0.8 }}>
                {user?.designation || 'Frontend Developer'}
              </span>
            </div>

            {/* CENTER */}
            <h1 className="main-title">📋 Daily Log</h1>

            {/* RIGHT */}
            <div className="header-actions">
              {!isCheckedIn && (
                <button
                  className={getCheckInButtonClass()}
                  onClick={handleCheckIn}
                  disabled={!canCheckIn() || checkingIn}
                >
                  {checkingIn ? 'Checking In...' : '✓ Check-In'}
                </button>
              )}

              {isCheckedIn && !isCheckedOut && (
                <div className="attendance-badge">
                  ✓ Checked In at {formatTime(dailyPlan.checkInTime)}
                </div>
              )}

              {isCheckedOut && (
                <div className="attendance-badge" style={{ background: '#d1fae5', color: '#065f46' }}>
                  ✅ Checked Out at {formatTime(dailyPlan.checkOutTime)}
                </div>
              )}

              <button onClick={logout} className="btn-logout">X</button>
            </div>

          </div>

          <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#e5e7eb', marginTop: '2px' }}>
            📅 {new Date().toLocaleDateString('en-IN', {
              weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
            })}
          </span>

          {/* <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}> */}
          <div style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: '10px'
          }}>
            {/*  Enter Logs: only when checked in AND logs not yet submitted AND not checked out */}
            {/* {isCheckedIn && !isLogsSubmitted && !isCheckedOut && (
              <button className="log-button" onClick={() => navigate('/logs')}>
                <span>📝</span> Enter Logs
              </button>
            )} */}

            <button
              className="log-button"
              style={{ backgroundColor: '#3b82f6', color: 'white' }}
              onClick={() => navigate('/my-logs')}
            >
              📋 My Logs
            </button>

            {isCheckedIn && !isCheckedOut && (
              <button className="log-button"
                style={{ backgroundColor: '#22c55e', color: 'white' }}
                onClick={() => navigate('/logs')}>
                <span>📝</span> Enter Logs
              </button>
            )}

            {/*  Checkout: only when logs submitted AND not yet checked out */}
            {/* {isLogsSubmitted && !isCheckedOut && ( */}
            {isLogsSubmitted && hasAtLeastOneLog && !isCheckedOut && (
              <button
                className="log-button"
                onClick={handleCheckOut}
                disabled={checkingOut}
                style={{ opacity: checkingOut ? 0.6 : 1 }}
              >
                {checkingOut ? 'Checking Out...' : '🚪 Check-Out'}
              </button>
            )}
          </div>
        </div>

        <div className="main-content">
          {/* Left Panel - Today's Plan */}
          <div className="panel left-panel">
            <div className="panel-header">
              <h2 className="panel-title">Today's Plan</h2>
              <span className={getTimeBadgeClass()}>
                {/* {getTotalPlannedHours().toFixed(1)} / 8 hrs */}
                {formatHoursToHHMM(getTotalPlannedHours())} / 8 hrs
              </span>
            </div>

            <div className="task-list">
              {dailyPlan?.tasks && dailyPlan.tasks.length > 0 ? (
                dailyPlan.tasks.map(task => (
                  <div
                    key={task.id}
                    onClick={() => {
                      setSelectedTask(task);
                      setShowTaskModal(true);
                    }}
                    className={task.isFreeTime ? 'task-item free-time-task' : 'task-item planned-task'}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="task-header">
                      <div>
                        <div className="task-title">{task.title}</div>
                        {task.backlogTask?.project?.name && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            📁 {task.backlogTask.project.name}
                          </div>
                        )}
                      </div>
                      {task.overdueDays > 0 && (
                        <span className="overdue-badge">
                          Overdue by {task.overdueDays} day{task.overdueDays > 1 ? 's' : ''}
                        </span>
                      )}
                      <span className={`task-priority priority-${task.priority}`}>
                        {task.isFreeTime ? 'FREE TIME' : task.priority?.toUpperCase()}
                      </span>
                    </div>
                    <div className="task-description">{task.description}</div>
                    <div className="task-footer">
                      <span className="task-type">
                        {task.type} • {formatHoursToHHMM(task.estimate)} hrs
                      </span>

                      <div className="task-actions">
                        {isCheckedIn && !isCheckedOut && (
                          <button
                            className="btn btn-enter-log btn-small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setQuickLogTask(task);
                            }}
                          >
                            Enter Log
                          </button>
                        )}

                        {!isCheckedOut && (
                          <button
                            className="btn btn-remove btn-small"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTaskFromPlan(task.id);
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <p>No tasks planned yet.<br />Add tasks from the backlog →</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Product Backlog */}
          <div className="panel right-panel">
            <div className="panel-header">
              <h2 className="panel-title">Product Backlog</h2>
            </div>

            <div className="project-selector">
              <label htmlFor="projectSelect">Select Project:</label>
              <select
                id="projectSelect"
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
              >
                <option value="">-- Choose a project --</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>

            {/*  Hide backlog actions after check-in */}
            {!isCheckedOut && (
              <div className="action-buttons">
                <button
                  className="btn btn-add"
                  onClick={() => setShowAddTaskModal(true)}
                  disabled={!selectedProject}
                >
                  <span>➕</span> Add New Task
                </button>
                <button className="btn btn-free-time" onClick={() => setShowFreeTimeModal(true)}>
                  <span>☕</span> Add Free Time
                </button>
              </div>
            )}

            <div className="task-list">
              {selectedProject ? (
                availableBacklogTasks.length > 0 ? (
                  availableBacklogTasks.map(task => (
                    <div key={task.id} className="task-item">
                      <div className="task-header">
                        <div className="task-title">{task.title}</div>
                        <span className={`task-priority priority-${task.priority}`}>
                          {task.priority.toUpperCase()}
                        </span>
                      </div>
                      <div className="task-description">{task.description}</div>
                      <div className="task-footer">
                        <span className="task-type">{task.type}</span>
                        {!isCheckedOut && (
                          <div className="estimate-input">
                            <label className="estimate-label">Estimate</label>
                            <select
                              value={estimates[task.id] ?? '60'}
                              onChange={(e) => setEstimates(prev => ({ ...prev, [task.id]: e.target.value }))}
                            >
                              <option value="15">15 min</option>
                              <option value="30">30 min</option>
                              <option value="45">45 min</option>
                              <option value="60">60 min</option>
                              <option value="90">90 min</option>
                              <option value="120">120 min</option>
                            </select>
                            {/* <button className="btn btn-add" onClick={() => addTaskToPlan(task)}>Add</button> */}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                className="btn btn-add"
                                onClick={() => addTaskToPlan(task)}
                              >
                                Add
                              </button>

                              <button
                                className="btn btn-edit"
                                onClick={() => {
                                  setSelectedTask(task);
                                  setShowAddTaskModal(true);
                                }}
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <p>No tasks in backlog<br />Add a new task to get started</p>
                  </div>
                )
              ) : (
                <div className="empty-state">
                  <p>Select a project to view backlog</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* {showAddTaskModal && (
        <AddTaskModal onClose={() => setShowAddTaskModal(false)} onSubmit={handleAddNewTask} />
      )} */}
      {showAddTaskModal && (
        <AddTaskModal
          onClose={() => {
            setShowAddTaskModal(false);
            setSelectedTask(null);
          }}
          onSubmit={handleAddNewTask}
          editTask={selectedTask}
        />
      )}

      {showFreeTimeModal && (
        <FreeTimeModal
          onClose={() => setShowFreeTimeModal(false)}
          onSubmit={handleAddFreeTime}
          currentTotal={getTotalPlannedHours()}
        />
      )}

      {/* Task detail modal */}
      {showTaskModal && selectedTask && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '25px',
            borderRadius: '12px',
            width: '420px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>

            {/* Heading */}
            <h2 style={{ marginBottom: '20px', textAlign: 'center' }}>
              Task Details
            </h2>

            {/* Content */}
            <div style={{ lineHeight: '1.8' }}>
              <p><strong>Task Name:</strong> {selectedTask.title || 'N/A'}</p>

              <p><strong>Project Name:</strong>
                {selectedTask.backlogTask?.project?.name || 'N/A'}
              </p>

              <p><strong>Description:</strong>
                {selectedTask.description || 'N/A'}
              </p>

              <p><strong>Estimate:</strong>
                {formatHoursToHHMM(selectedTask.estimate)} hrs
              </p>

              <p><strong>Type:</strong> {selectedTask.type || 'N/A'}</p>

              <p><strong>Priority:</strong> {selectedTask.priority || 'N/A'}</p>

              {selectedTask.actualHours > 0 && (
                <p><strong>Logged Hours:</strong> {selectedTask.actualHours} hrs</p>
              )}
            </div>

            {/* Button */}
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                className="btn btn-cancel"
                onClick={() => setShowTaskModal(false)}
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {quickLogTask && (
        <QuickLogModal
          task={quickLogTask}
          onClose={() => setQuickLogTask(null)}
          onSaved={loadTodayPlan}
        />
      )}
    </div>
  );
};

export default TaskPlanner;